import type { ScreenFeatureResource } from './transport'

/**
 * - `not-found`: the merged config carries no block for this entry.
 * - `server`: any other refusal or failure the server (or the network) answered.
 * - `invalid`: the server answered a block that fails the entry's schema.
 * - `rejected`: the module refused before calling the server (no school, read
 *   not ready, fleet-default target on an entry that does not accept one).
 */
type ScreenFeatureErrorKind = 'not-found' | 'server' | 'invalid' | 'rejected'

export class ScreenFeatureError extends Error {
  readonly kind: ScreenFeatureErrorKind
  readonly status?: number
  readonly type?: string

  constructor(kind: ScreenFeatureErrorKind, message: string, details: { status?: number; type?: string } = {}) {
    super(message)
    this.name = 'ScreenFeatureError'
    this.kind = kind
    this.status = details.status
    this.type = details.type
  }
}

/**
 * The error type a get-single answers when the merged config lacks the block.
 * The use-cases return `FEATURE_NOT_FOUND` / `SCREEN_NOT_FOUND` and the handlers
 * add the `SYSTEM_` prefix. `FEATURE_NOT_FOUND` arrives as a 404, but
 * `SCREEN_NOT_FOUND` has no HTTP mapping and arrives as a 500, so only the type
 * tells "no block" from a real failure. A global feature whose schema has a
 * `{}` default is answered with that default instead of not-found.
 */
const NOT_FOUND_TYPE: Record<ScreenFeatureResource, string> = {
  global: 'SYSTEM_FEATURE_NOT_FOUND',
  screen: 'SYSTEM_SCREEN_NOT_FOUND'
}

/** What the server answered for a refused or failed call. */
export interface ServerRefusal {
  status: number
  /** The backend's `error.type`, such as `'SYSTEM_INVALID_REQUEST'`. */
  type?: string
  message: string
}

/**
 * The one mapping from a server refusal to a `ScreenFeatureError`, used by the
 * HTTP adapter (from plan 01's `ApiError`) and by the fake transport, so the
 * two cannot disagree on what "not found" is.
 */
export function screenFeatureErrorFrom(resource: ScreenFeatureResource, refusal: ServerRefusal): ScreenFeatureError {
  const details = { status: refusal.status, type: refusal.type }
  if (refusal.type === NOT_FOUND_TYPE[resource]) {
    return new ScreenFeatureError('not-found', refusal.message, details)
  }
  return new ScreenFeatureError('server', refusal.message, details)
}
